import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { TOKEN_REFETCH_DURATION } from "@/libs/constants/auth";
import { useAppSelector, useAppStore } from "@/redux/hooks";
import getFreshTokenBeforeSixtyMinutes from "@/redux/slice/auth/actions/getFreshTokenBeforeSixtyMinutes";
import updateSSOTokenFromAuthService from "@/redux/slice/auth/actions/updateSSOTokenFromAuthService";
import authTokenSelector from "@/redux/slice/auth/selectors/authToken";
import ssoTokenSelector from "@/redux/slice/auth/selectors/ssoToken";
import { updateAuthToken, updateNewUser, updateSSOToken } from "@/redux/slice/auth/slice";
import { SignInActions } from "@/redux/slice/auth/types/SignInActions";
import firebaseRepo from "@/libs/firebase";
import userConsentSelector from "@/redux/slice/auth/selectors/userConsent";
import userThunkStatusSelector from "@/redux/slice/auth/selectors/userThunkStatus";
import consentAuthDataSelector from "@/redux/slice/auth/selectors/consentAuthData";
import tokenRecievedFetchPatientsSelector from "@/redux/slice/auth/selectors/tokenRecievedFetchPatients";
import recievedTokenFetchPatients from "@/redux/slice/auth/actions/recievedTokenFetchPatients";
import fetchUserType from "@/redux/slice/auth/thunks/fetchUserType";
import newUserSelector from "@/redux/slice/auth/selectors/newUser";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import refererSelector from "@/redux/slice/global/selectors/refererSelector";
import { loginDoneEvent } from "@/helpers/TSDEvents";
import { LOGIN_EVENT_KEY } from "@/helpers/constants";
import { ThunkResolvedStatus } from "@/libs/enum/ThunkResolvedStatus";

const useManageAuth = (dispatch, apolloClient, ua, xAuthToken, xMobileNumber) => {
  const ssoToken = useAppSelector(ssoTokenSelector);
  const userConsent = useAppSelector(userConsentSelector);
  const consentAuthData = useAppSelector(consentAuthDataSelector);
  const authToken = useAppSelector(authTokenSelector);
  const tokenRecievedFetchPatients = useAppSelector(tokenRecievedFetchPatientsSelector);
  const currentPatient = useAppSelector(currentPatientSelector);
  const newUser = useAppSelector(newUserSelector);
  const navSrcSelector = useAppSelector(refererSelector);
  const userThunk = useAppSelector(userThunkStatusSelector);
  const store = useAppStore();

  useEffect(() => {
    const mobileNumber = localStorage.getItem("mobile_num") || "";
    const hasMobileNumber = mobileNumber?.length > 0 && mobileNumber !== "null";
    if (!hasMobileNumber || !tokenRecievedFetchPatients) return;
    recievedTokenFetchPatients({
      isNewUser: newUser,
      apolloClient,
      mobileNumber,
      store,
      dispatch,
      authToken,
    });
  }, [tokenRecievedFetchPatients]);

  useEffect(() => {
    const handleMobileAutoLogin = (utm_token_param: string, refresh_token_param: string, utm_mobile_param: string) => {
      const idToken = utm_token_param || "";
      const refreshToken = refresh_token_param || "";
      // Mobile APP Autologin Case

      if (utm_mobile_param?.trim()?.length) {
        const mobileNumber =
          utm_mobile_param.trim()[0] === "+" ? `${utm_mobile_param.trim()}` : `+${utm_mobile_param.trim()}`;
        localStorage.removeItem("CIRCLEPlan");
        localStorage.setItem("mobile_num", mobileNumber);

        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
          dispatch(updateSSOToken("true"));
        } else if (idToken) {
          // THIS FLOW SHOULD BE DEPRECATED AS SOON AS POSSIBLE
          // WRONG DEFINITION BELOW BUT MAKES THE WEBVIEW WORK IN NATIVE APPS.
          localStorage.setItem("refreshToken", idToken);
          dispatch(updateAuthToken(idToken));
        }
      }
    };

    const params = new URLSearchParams(window?.location?.search);
    const utm_token_param = xAuthToken || params.get("utm_token");
    const refresh_token_param = params.get("refresh_token");
    const utm_mobile_param = xMobileNumber || params.get("utm_mobile_number");
    const mobileAutoLogin =
      !!window?.location?.search?.length && (utm_token_param || refresh_token_param) && utm_mobile_param;

    if (mobileAutoLogin) {
      handleMobileAutoLogin(utm_token_param, refresh_token_param, utm_mobile_param);
    } else {
      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken?.length && refreshToken !== "undefined" && refreshToken !== "null") {
        // means that refresh token is in the data layer. Set a value to SSOToken
        // so that the other useEffect fires to get an id_token from firebase.
        // In that useEffect first it is checked if the data layer has a refresh token
        dispatch(updateSSOToken("true"));
      } else {
        updateSSOTokenFromAuthService(dispatch);
      }
    }

    return () => {};
  }, []);

  useEffect(() => {
    // this is for the after login check/update the token
    if (!ssoToken) return;
    const refreshToken = localStorage.getItem("refreshToken");
    const signInActions: SignInActions = {
      dispatchUpdateAuthToken(token) {
        dispatch(updateAuthToken(token));
      },
      dispatchUpdateNewUser(isNewUser) {
        dispatch(updateNewUser(isNewUser));
      },
    };

    if (refreshToken?.length && refreshToken !== "undefined" && refreshToken !== "null") {
      getFreshTokenBeforeSixtyMinutes({
        apolloClient,
        tokenJustGotRefetched: false,
        dispatch,
      });
    } else {
      firebaseRepo.getInstance().customSignIn(ssoToken, signInActions);
    }

    setInterval(() => {
      getFreshTokenBeforeSixtyMinutes({
        apolloClient,
        tokenJustGotRefetched: true,
        dispatch,
      });
    }, TOKEN_REFETCH_DURATION);
  }, [ssoToken]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem("authToken", authToken);
    };
  }, [authToken]);

  useEffect(() => {
    if (currentPatient?.mobileNumber) {
      dispatch(
        fetchUserType({
          apolloClient,
          mobileNumber: currentPatient?.mobileNumber,
        })
      );
    }
  }, [currentPatient?.id]);

  useEffect(() => {
    if (userThunk?.fetchUserType === ThunkResolvedStatus.fulfilled && localStorage.getItem(LOGIN_EVENT_KEY) !== null) {
      loginDoneEvent({
        mobile: currentPatient?.mobileNumber,
        gender: currentPatient?.gender,
        platform: ua?.isDesktop ? "Desktop" : "mobile",
        nav_src: navSrcSelector,
        is_new_user: newUser,
      });
      localStorage.removeItem(LOGIN_EVENT_KEY);
    }
  }, [userThunk]);

  useEffect(() => {
    if (userConsent) {
      if (consentAuthData?.askForConsent || (!consentAuthData?.status && !consentAuthData?.authToken)) {
        // router.push(clientRoutes.signIn(clientRoutes.medicines()));
      } else {
        localStorage.setItem("mobile_num", consentAuthData?.mobileNumber);
        localStorage.setItem("currentUser", consentAuthData?.patientId || "");
        // from a previous flow the refreshToken could be set.
        localStorage.removeItem("refreshToken");
        dispatch(updateSSOToken(consentAuthData?.authToken));

        return;
      }
    }
  }, [userConsent]);
};

export default useManageAuth;
